/**
 * Client Portal — Playback Sessions page.
 * View scheduled playback sessions and submit feedback.
 */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, List, ListItemButton,
} from '@mui/material';
import { PlayCircle, Feedback, Slideshow, CheckCircle } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';
import PortalTooltip from '../../../components/PortalTooltip';

interface PlaybackSessionItem {
  id: string;
  assessment_id: string;
  scheduled_date: string | null;
  scheduled_end_date: string | null;
  scheduled_timezone: string | null;
  meeting_link: string | null;
  status: string;
  notes: string | null;
  created_at: string | null;
  has_pptx?: boolean;
  report_id?: string;
  has_submitted_feedback?: boolean;
  report_status?: string;
}

interface PresentationPreviewBlock {
  text: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

interface PresentationPreviewSlide {
  index: number;
  title: string;
  blocks: PresentationPreviewBlock[];
  summary: string;
}

/** Validate the authenticated preview response before rendering server content. */
function isPresentationPreview(value: unknown): value is { success: true; slides: PresentationPreviewSlide[] } {
  if (!value || typeof value !== 'object' || !('success' in value) || value.success !== true
      || !('slides' in value) || !Array.isArray(value.slides)) return false;
  return value.slides.every((slide: unknown) =>
    !!slide && typeof slide === 'object'
    && 'index' in slide && typeof slide.index === 'number' && Number.isInteger(slide.index)
    && 'title' in slide && typeof slide.title === 'string'
    && 'summary' in slide && typeof slide.summary === 'string'
    && 'blocks' in slide && Array.isArray(slide.blocks)
    && slide.blocks.every((block: unknown) =>
      !!block && typeof block === 'object'
      && 'text' in block && typeof block.text === 'string'
      && ['left', 'top', 'width', 'height'].every(key =>
        key in block && typeof Reflect.get(block, key) === 'number'
        && Number.isFinite(Reflect.get(block, key)))
    )
  );
}

/** Extract HH:MM time string from a UTC ISO date in a given IANA timezone. */
function timeInZone(isoUtc: string, tz: string): string {
  const dt = new Date(isoUtc);
  if (isNaN(dt.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(dt);
  const h = parts.find((p: Intl.DateTimeFormatPart) => p.type === 'hour')?.value ?? '00';
  const m = parts.find((p: Intl.DateTimeFormatPart) => p.type === 'minute')?.value ?? '00';
  return `${h}:${m}`;
}

/** Format a UTC ISO date for display in a given IANA timezone (DD/MM/YYYY HH:MM). */
function displayInZone(isoUtc: string, tz: string): string {
  const dt = new Date(isoUtc);
  if (isNaN(dt.getTime())) return '\u2014';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(dt);
}

const PlaybackSessions: React.FC = () => {
  const [sessions, setSessions] = useState<PlaybackSessionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [feedbackDialog, setFeedbackDialog] = useState<PlaybackSessionItem | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [pptxViewSession, setPptxViewSession] = useState<PlaybackSessionItem | null>(null);
  const [pptxLoading, setPptxLoading] = useState(false);
  const [previewSlides, setPreviewSlides] = useState<PresentationPreviewSlide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(1);
  const previewRequest = useRef(0);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const closePresentation = () => {
    previewRequest.current += 1;
    setPptxViewSession(null);
    setPreviewSlides([]);
    setCurrentSlide(1);
    setPreviewError(null);
  };

  useEffect(() => () => { previewRequest.current += 1; }, []);

  const fetchPlaybackSessions = useCallback(() => {

    setLoading(true);
    authFetch('/api/client/playback-sessions')
      .then((r) => r.json())
      .then((data) => { if (data.success) setSessions(data.sessions); })
      .catch(() => setError('Failed to load playback sessions'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });

  }, []);

  useEffect(() => { fetchPlaybackSessions(); }, [fetchPlaybackSessions]);

  const handleViewPptx = async (session: PlaybackSessionItem) => {
    const requestId = ++previewRequest.current;
    setPptxViewSession(session);
    setPptxLoading(true);
    setPreviewError(null);
    setPreviewSlides([]);
    setCurrentSlide(1);
    try {
      const response = await authFetch(`/api/client/playback-sessions/${encodeURIComponent(session.id)}/presentation/preview`);
      const data: unknown = await response.json();
      if (!response.ok || !isPresentationPreview(data)) {
        throw new Error('The presentation could not be loaded. Please retry or contact your KPMG team.');
      }
      if (requestId === previewRequest.current) {
        setPreviewSlides(data.slides);
        if (data.slides.length === 0) setPreviewError('No readable slides are available. Contact your KPMG team.');
      }
    } catch {
      if (requestId === previewRequest.current) {
        setPreviewError('The presentation could not be loaded. Please retry or contact your KPMG team.');
      }
    } finally {
      if (requestId === previewRequest.current) setPptxLoading(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!feedbackDialog) return;
    try {
      const response = await authFetch(`/api/client/playback-sessions/${feedbackDialog.id}/feedback`, {
        method: 'POST',
        body: JSON.stringify({ feedback_text: feedbackText }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setError(err.error || 'Failed to submit feedback');
        return;
      }
      setFeedbackSent(true);
      setTimeout(() => {
        setFeedbackDialog(null);
        setFeedbackSent(false);
        setFeedbackText('');
        fetchPlaybackSessions();
      }, 2000);
    } catch {
      setError('Failed to submit feedback');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'info';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <PortalTooltip tooltipKey="client.page.playback" showIcon>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Playback Sessions
          </Typography>
        </PortalTooltip>
        <RefreshButton onRefresh={fetchPlaybackSessions} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Scheduled sessions where KPMG SMEs review your assessment results with your team.
      </Typography>

      {sessions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No playback sessions scheduled.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Meeting Link</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    {s.scheduled_date
                      ? `${displayInZone(s.scheduled_date, s.scheduled_timezone || 'UTC')} (${s.scheduled_timezone || 'UTC'})`
                      : '\u2014'}
                  </TableCell>
                  <TableCell>
                    {s.scheduled_end_date
                      ? timeInZone(s.scheduled_end_date, s.scheduled_timezone || 'UTC')
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip label={s.status} size="small" color={getStatusColor(s.status) as any} />
                  </TableCell>
                  <TableCell>
                    {s.meeting_link ? (
                      <Button size="small" href={s.meeting_link} target="_blank" startIcon={<PlayCircle />}>
                        Join
                      </Button>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{s.notes || '—'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {(s.status === 'scheduled' || s.status === 'playback_scheduled') && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Slideshow />}
                          sx={{ bgcolor: '#00338D' }}
                          onClick={() => handleViewPptx(s)}
                        >
                          View Presentation
                        </Button>
                      )}
                      {(s.status === 'completed' || s.status === 'in_progress') && (
                        s.report_status === 'final_uploaded' ? (
                          <Chip
                            size="small"
                            icon={<CheckCircle />}
                            label="Report Finalized"
                            color="default"
                            variant="outlined"
                          />
                        ) : s.has_submitted_feedback ? (
                          <Chip
                            size="small"
                            icon={<CheckCircle />}
                            label="Feedback Submitted"
                            color="success"
                            variant="outlined"
                          />
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Feedback />}
                            onClick={() => setFeedbackDialog(s)}
                          >
                            Submit Feedback
                          </Button>
                        )
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Feedback Dialog */}
      <Dialog open={!!feedbackDialog} onClose={() => setFeedbackDialog(null)} maxWidth="md" fullWidth>
        <DialogTitle>Submit Playback Feedback</DialogTitle>
        <DialogContent>
          {feedbackSent ? (
            <Alert severity="success" sx={{ mt: 1 }}>Feedback submitted successfully! The KPMG team will review and incorporate it into your final report.</Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Provide your feedback on the assessment draft reviewed during the playback session.
                This feedback will be incorporated into the final report.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Your feedback"
                placeholder="Share your observations, corrections, or additional context..."
                value={feedbackText}
                onChange={(e) => setFeedbackText(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFeedbackDialog(null)}>Cancel</Button>
          {!feedbackSent && (
            <Button variant="contained" onClick={handleSubmitFeedback} disabled={!feedbackText.trim()}>
              Submit Feedback
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Protected PPT Viewer Dialog */}
      <Dialog
        open={!!pptxViewSession}
        onClose={closePresentation}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Assessment Presentation — View Only</DialogTitle>
        <DialogContent
          sx={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
          }}
          onContextMenu={(e) => e.preventDefault()}
          onCopy={(e) => e.preventDefault()}
        >
          {pptxLoading ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Loading presentation...
              </Typography>
            </Box>
          ) : null}
          
          <Box sx={{ position: 'relative', display: pptxLoading ? 'none' : 'block' }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              View-only text preview. Images, charts and original slide formatting may be omitted.
              No original-file download is provided here. Viewing cannot prevent screenshots.
            </Alert>
            {previewError && <Alert severity="error">{previewError}</Alert>}

            <Box sx={{ height: '70vh', overflow: 'auto', backgroundColor: '#f5f5f5' }}>
              <List aria-label="Presentation slides" sx={{ display: 'flex', flexWrap: 'wrap' }}>
                {previewSlides.map((slide, index) => (
                  <ListItemButton key={index} selected={currentSlide === index + 1}
                    onClick={() => setCurrentSlide(index + 1)} sx={{ flex: '0 0 auto' }}>
                    Slide {index + 1}
                  </ListItemButton>
                ))}
              </List>
              {previewSlides[currentSlide - 1] && (
                <Paper sx={{ p: 3, m: 2 }} aria-label={`Slide ${currentSlide} content`}>
                  <Typography variant="h5" gutterBottom>{previewSlides[currentSlide - 1].title}</Typography>
                  {previewSlides[currentSlide - 1].blocks.map((block, index) => (
                    <Typography key={index} sx={{ mb: 2, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {block.text}
                    </Typography>
                  ))}
                </Paper>
              )}
            </Box>

            {/* Confidentiality watermark; not screenshot prevention. */}
            <Box
              sx={{
                position: 'absolute',
                top: 60,
                left: 0,
                right: 0,
                bottom: 0,
                pointerEvents: 'none',
                opacity: 0.06,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                transform: 'rotate(-30deg)',
                overflow: 'hidden',
              }}
            >
              {Array.from({ length: 12 }).map((_, i) => (
                <Typography key={i} variant="h4" sx={{ color: '#00338D', fontWeight: 700, whiteSpace: 'nowrap' }}>
                  KPMG CONFIDENTIAL
                </Typography>
              ))}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePresentation}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlaybackSessions;
